/**
 * validators.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators
} from "@angular/forms";

// TODO: why the heck is this squiggly???????
import "zxcvbn";

import { inputless, valueless } from "./utilities/functions";

interface VError {
    [key : string] : any;
}

export class CustomValidators
{
    public static nullOr (fn : ValidatorFn | ValidatorFn[]) : ValidatorFn
    {
        if (!Array.isArray(fn))
        {
            fn = [fn];
        }
        return (control : AbstractControl) => (
            (control.value === null)
            ? null
            : Validators.compose(fn as ValidatorFn[])(control)
        );
    }

    public bubbles (fn : ValidatorFn) : ValidatorFn
    {
        return (control : AbstractControl) => {
            const result : VError = fn(control);
            if (result)
            {
                const errors : VError = control.root.errors;
                Object.assign(errors, {bubbled: result});
                control.root.setErrors(errors);
            }
            return result;
        };
    }

    public static childErrors (group : FormGroup) : {[key : string] : any}
    {
        console.log("Running childErrors");
        const aggregate : {[error : string] : any} = {};
        let hasErrors : boolean = false;
        for (const control of Object.values(group.controls))
        {
            console.log(control);
            if (control.errors)
            {
                Object.assign(aggregate, control.errors);
                hasErrors = true;
            }
        }
        return hasErrors ? aggregate : null;
    }

    public static number (control : AbstractControl) : {number : boolean}
    {
        return (Number.isFinite(control.value)) ? null : {number: true};
    }

    public static integer (control : AbstractControl) : {integer : boolean}
    {
        return (Number.isInteger(control.value)) ? null : {integer: true};
    }

    public static safeInteger (control : AbstractControl)
        : {safeInteger : string}
    {
        if (control.value > Number.MAX_SAFE_INTEGER)
        {
            return {safeInteger: "above"};
        }
        return (
            (control.value < Number.MIN_SAFE_INTEGER)
            ? {safeInteger: "below"}
            : null
        );
    }

    public static requiredList (control : FormArray)
        : {requiredList : boolean}
    {
        return (
            control.controls.some(
                (c : AbstractControl) => inputless(c.value))
            ? {requiredList: true}
            : null
        );
    }

    public static onlyLastEmpty (control : FormArray)
        : {onlyLastEmpty : boolean}
    {
        return (
            control.controls.slice(0, -1).some(
                (c : AbstractControl) => inputless(c.value))
            ? {onlyLastEmpty: true}
            : null
        );
    }

    public static noInternalNulls (control : FormArray)
        : {noInternalNulls : number}
    {
        const firstBlank : number = control.controls.findIndex(
            (c : AbstractControl) => inputless(c.value));
        if (firstBlank === -1)
        {
            return null;
        }
        return (
            (control.controls.slice(firstBlank).every(
                (c : AbstractControl) => inputless(c.value)))
            ? null
            : {noInternalNulls: firstBlank}
        );
    }

    public static min (min : number) : ValidatorFn
    {
        return (control : AbstractControl) =>
            (control.value >= min ? null : {min: true});
    }

    public static max (max : number) : ValidatorFn
    {
        return (control : AbstractControl) =>
            (control.value <= max ? null : {max: true});
    }

    public static date (control : AbstractControl) : {date : boolean}
    {
        return (control.value instanceof Date) ? null : {date: true};
    }

    public static boolean (control : AbstractControl) : {boolean : boolean}
    {
        return (typeof control.value === "boolean") ? null : {boolean : true};
    }

    public static arrayMin (min : number) : ValidatorFn
    {
        return (control : AbstractControl) =>
            ((Array.isArray(control.value) && control.value.length >= min)
                ? null
                : {arrayMin: true});
    }

    public static in (options : any) : ValidatorFn
    {
        return (control : AbstractControl) => {
            if (valueless(control.value))
            {
                return null;
            }
            return (
                CustomValidators.contains(control.value, options)
                ? null
                : {in: options.value}
            );
        };
    }

    public static subset (options : any) : ValidatorFn
    {
        return (control : AbstractControl) => {
            if (valueless(control.value))
            {
                return null;
            }
            if (!Array.isArray(control.value))
            {
                return {subset: "Value is not an array!"};
            }
            return (
                control.value.every((value : any) =>
                    CustomValidators.contains(value, options))
                ? null
                : {subset: false}
            );
        };
    }

    private static contains (value : any, options : any[]) : boolean
    {
        if (options.indexOf(value) === -1)
        {
            console.error(
                "could not find value in options", value, options);
            return false;
        }
        return true;
    }

    /**
     * <ADAPTED FROM ANGULAR 4>
     * Validator that compares the value of the given FormControls
     */
    public static equalsTo (...controls : AbstractControl[]) : ValidatorFn
    {
        return (control : FormControl) : {[key : string] : any} => {
            if (controls.length < 1) {
                throw new Error("You must compare to at least 1 other field");
            }

            for (const compare of controls) {
                if (compare.value !== control.value) {
                    return {equalsTo: {unequalField: compare}};
                }
            }
            return null;
        };
    }

    public static zxcvbnMagnitude (minimum : number) : ValidatorFn
    {
        return (c : FormControl) => {
            const result : ZxcvbnResult = Zxcvbn(c.value);
            if (result.guesses_log10 < minimum) {
                return {guessMagnitude: result.guesses_log10};
            }
            return null;
        };
    }

}

export function anyChildHasError (coll : AbstractControl, err : string)
    : boolean
{
    if (coll instanceof FormArray)
    {
        return coll.controls.some((c : AbstractControl) => c.hasError(err));
    }
    if (coll instanceof FormGroup)
    {
        return Object.values(coll.controls).some(
            (c : AbstractControl) => c.hasError(err));
    }
    return coll.hasError(err);
}

interface ErrorAggregate {
    [key : string] : {[errName : string] : any};
}

export function childErrors (coll : AbstractControl) : ErrorAggregate
{
    if (coll instanceof FormArray)
    {
        const errors : ErrorAggregate = {};
        coll.controls.forEach((ctrl : AbstractControl, i : number) => {
            if (ctrl.errors)
            {
                errors[i] = ctrl.errors;
            }
        });
        return errors;
    }
    if (coll instanceof FormGroup)
    {
        const errors : ErrorAggregate = {};
        Object.entries(coll.controls).forEach(
            ([key, ctrl] : [string, AbstractControl]) => {
                if (ctrl.errors)
                {
                    errors[key] = ctrl.errors;
                }
            }
        );
        return errors;
    }
    return {this: coll.errors};
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/validators.ts